/**
 * PDF Export Styles and Constants
 * 
 * Two style configurations:
 * - SLIDESHOW: Bold colors, larger text for presentations on big screens
 * - PRINTABLE: Subtle colors, smaller text for printing/email
 */

// ============================================================================
// SLIDESHOW STYLES (bold, large - for presentations)
// ============================================================================

// Care setting colors - bold/saturated for visibility on projectors
export const SLIDESHOW_LOCATION_TYPE_COLORS = {
  0: [220, 100, 100],   // All - darker red
  1: [55, 95, 180],     // Ambulatory Surgery Center - deeper blue
  2: [0, 180, 130],     // Outpatient - deeper teal
  3: [230, 160, 60],    // Office - deeper orange/yellow
  4: [220, 70, 70],     // Inpatient - deeper red
  5: [20, 160, 200],    // Lab - deeper cyan
  6: [90, 60, 200],     // Skill Nursing Facility - deeper purple
  7: [30, 100, 200],    // Telehealth - deeper blue
  8: [0, 180, 130],     // Home - deeper teal
  9: [220, 180, 60],    // Other - deeper yellow
  10: [130, 40, 160],   // Urgent Care - deeper purple
};

export const SLIDESHOW_SETTINGS = {
  margin: {
    top: 40,
    right: 40,
    bottom: 15,
    left: 40,
  },
  header: {
    titleFontSize: 14,
    subtitleFontSize: 11,
    dateFontSize: 9,
    filterFontSize: 9,
  },
  logo: {
    maxWidth: 100,
    maxHeight: 25,
  },
  chart: {
    barHeight: 18,
    barGap: 4,
    fontSize: {
      name: 9,
      stats: 8,
      legend: 8,
    },
    statsReservedWidth: 150,
    footerHeight: 28,
    legendHeight: 18,
  },
  footer: {
    height: 20,
    fontSize: 8,
  },
  colors: SLIDESHOW_LOCATION_TYPE_COLORS,
};

// ============================================================================
// PRINTABLE STYLES (subtle, compact - for printing/email)
// ============================================================================

// Care setting colors - lighter/softer for print
export const PRINTABLE_LOCATION_TYPE_COLORS = {
  0: [255, 150, 150],   // All - light red
  1: [83, 125, 201],    // Ambulatory Surgery Center - medium blue
  2: [2, 227, 157],     // Outpatient - teal
  3: [255, 191, 105],   // Office - light orange
  4: [255, 107, 107],   // Inpatient - light red
  5: [37, 195, 233],    // Lab - light cyan
  6: [120, 90, 220],    // Skill Nursing Facility - light purple
  7: [70, 140, 220],    // Telehealth - light blue
  8: [2, 227, 157],     // Home - teal
  9: [255, 210, 100],   // Other - light yellow
  10: [153, 55, 186],   // Urgent Care - purple
};

export const PRINTABLE_SETTINGS = {
  margin: {
    top: 30,
    right: 30,
    bottom: 15,
    left: 30,
  },
  header: {
    titleFontSize: 10,
    subtitleFontSize: 8,
    dateFontSize: 7,
    filterFontSize: 7,
  },
  logo: {
    maxWidth: 70,
    maxHeight: 18,
  },
  chart: {
    barHeight: 12,
    barGap: 3,
    fontSize: {
      name: 7,
      stats: 6,
      legend: 6,
    },
    statsReservedWidth: 110,
    footerHeight: 22,   // 18 + 2 yellow bar + buffer
    legendHeight: 16,
  },
  footer: {
    height: 14,
    fontSize: 6,
  },
  colors: PRINTABLE_LOCATION_TYPE_COLORS,
};

// ============================================================================
// SHARED / LEGACY (for backward compatibility)
// ============================================================================

// Default to slideshow colors for backward compatibility
export const LOCATION_TYPE_COLORS = SLIDESHOW_LOCATION_TYPE_COLORS;

// Location type names (should match LocationTypeUtil.getAll())
export const LOCATION_TYPE_NAMES = {
  0: 'All',
  1: 'Ambulatory Surgery Center',
  2: 'Outpatient',
  3: 'Office',
  4: 'Inpatient',
  5: 'Lab',
  6: 'Skill Nursing Facility',
  7: 'Telehealth',
  8: 'Home',
  9: 'Other',
  10: 'Urgent Care',
};

// Legend entries for slideshow (bold colors)
export const SLIDESHOW_LEGEND = [
  { id: 1, name: 'Ambulatory Surgery Center', color: [55, 95, 180] },
  { id: 2, name: 'Outpatient', color: [0, 180, 130] },
  { id: 3, name: 'Office', color: [230, 160, 60] },
  { id: 4, name: 'Inpatient', color: [220, 70, 70] },
  { id: 5, name: 'Lab', color: [20, 160, 200] },
  { id: 10, name: 'Urgent Care', color: [130, 40, 160] },
];

// Legend entries for printable (subtle colors)
export const PRINTABLE_LEGEND = [
  { id: 1, name: 'Ambulatory Surgery Center', color: [83, 125, 201] },
  { id: 2, name: 'Outpatient', color: [2, 227, 157] },
  { id: 3, name: 'Office', color: [255, 191, 105] },
  { id: 4, name: 'Inpatient', color: [255, 107, 107] },
  { id: 5, name: 'Lab', color: [37, 195, 233] },
  { id: 10, name: 'Urgent Care', color: [153, 55, 186] },
];

// Default legend for backward compatibility
export const LOCATION_TYPES_FOR_LEGEND = SLIDESHOW_LEGEND;

// Default color if location_type not found
export const DEFAULT_BAR_COLOR = [230, 160, 60]; // Office orange/yellow

// Get color for a location type (with optional colors map)
export const getLocationTypeColor = (locationType, colors = LOCATION_TYPE_COLORS) => {
  return colors[locationType] || DEFAULT_BAR_COLOR;
};

// Get legend array by pdfType
export const getLegendByType = (pdfType) => {
  return pdfType === 'slideshow' ? SLIDESHOW_LEGEND : PRINTABLE_LEGEND;
};

// Affiliated provider indicator color (teal)
export const AFFILIATED_COLOR = [2, 227, 157];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get settings configuration by PDF type
 * @param {string} pdfType - 'slideshow' or 'printable'
 * @returns {Object} - Settings configuration
 */
export const getSettingsByType = (pdfType) => {
  return pdfType === 'slideshow' ? SLIDESHOW_SETTINGS : PRINTABLE_SETTINGS;
};

/**
 * Get colors configuration by PDF type
 * @param {string} pdfType - 'slideshow' or 'printable'
 * @returns {Object} - Colors map
 */
export const getColorsByType = (pdfType) => {
  return pdfType === 'slideshow' ? SLIDESHOW_LOCATION_TYPE_COLORS : PRINTABLE_LOCATION_TYPE_COLORS;
};

// Legacy PDF_SETTINGS - defaults to slideshow for backward compatibility
export const PDF_SETTINGS = {
  ...SLIDESHOW_SETTINGS,
  // Add table styling (not in per-type settings)
  table: {
    fontSize: 8,
    headerFontSize: 9,
    cellPadding: 4,
    headerBgColor: [38, 70, 83],
    alternateBgColor: [248, 250, 252],
  },
  // Add text colors (shared)
  colors: {
    text: {
      primary: [33, 33, 33],
      secondary: [100, 100, 100],
      muted: [150, 150, 150],
      white: [255, 255, 255],
    },
    divider: [200, 200, 200],
  },
};

export default {
  LOCATION_TYPE_COLORS,
  LOCATION_TYPE_NAMES,
  LOCATION_TYPES_FOR_LEGEND,
  getLocationTypeColor,
  getSettingsByType,
  getColorsByType,
  getLegendByType,
  AFFILIATED_COLOR,
  PDF_SETTINGS,
  SLIDESHOW_SETTINGS,
  PRINTABLE_SETTINGS,
};